import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { etichettePattaBloccata } from '../../utils/chiave-rifiuto-patta';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { useChatStore } from '../../store/chat-store';
import { useAuthStore } from '../../store/auth-store';
import { useGameStore } from '../../store/game-store';
import { useBlindStore } from '../../store/blind-store';
import BlindPeekSummary from '../game/BlindPeekSummary';
import type { Move } from '@draughts/shared';
import { Color, GameStatus, resolveTimeControl } from '@draughts/shared';
import { getRatingForGame } from '../../utils/rating';
import { endReasonText } from '../../utils/end-reason-text';
import { isGuestUsername } from '../../utils/guest';
import { trackRematchRequested, trackGameCompleted } from '../../utils/analytics';
import { Eye, List, MessageSquare } from 'lucide-react';
import GameScreen, { type MobilePanel } from '../game/GameScreen';
import { useGuardiaSincronia } from '../../hooks/useGuardiaSincronia';
import GameSidebarTabs from '../game/GameSidebarTabs';
import MoveList from '../game/MoveList';
import ChatPanel from './ChatPanel';
import SpectatorsPanel from '../game/SpectatorsPanel';
import { useGameSpectators } from '../../store/spectators-store';
import RematchPanel from './RematchPanel';
import IncomingRematchPrompt from './IncomingRematchPrompt';
import ReturnLegCountdown from './ReturnLegCountdown';
import GameOverOverlay from './GameOverOverlay';
import DrawnOpeningBanner from './DrawnOpeningBanner';
import TakebackNotice from './TakebackNotice';
import ConnessionePersaBanner from './ConnessionePersaBanner';
import { useSessioneAltrove } from '../../hooks/useSessioneAltrove';
import AvvisoErrore from './AvvisoErrore';
import { log } from '../../utils/logger';
import { DRAW_DECLINE_NOTICE_MS } from '../../constants/timing';
import './OnlineGame.css';

interface OnlineGameProps {
  onLeave: () => void;
}

/**
 * Ogni quanto si richiede lo stato vero, finche' resta incoerente.
 *
 * Piu' lungo dell'avviso a schermo (che compare a 2 s): prima si dice
 * all'utente cosa sta succedendo, poi si prova a ripararlo da soli. E si
 * continua a provare: chi sta senza rete non ha un momento preciso in cui
 * torna, e un colpo solo lo si spreca quasi sempre.
 */
const ATTESA_PRIMA_DEL_RESYNC_MS = 8000;

function OnlineGame({ onLeave }: OnlineGameProps) {
  const { t } = useTranslation();
  const status = useMultiplayerStore((state) => state.status);
  const roomId = useMultiplayerStore((state) => state.roomId);
  const playerColor = useMultiplayerStore((state) => state.playerColor);
  const boardState = useMultiplayerStore((state) => state.boardState);
  const gameResult = useMultiplayerStore((state) => state.gameResult);
  const gameEndReason = useMultiplayerStore((state) => state.gameEndReason);
  const opponent = useMultiplayerStore((state) => state.opponent);
  const sessionScore = useMultiplayerStore((state) => state.sessionScore);
  const opponentWantsRematch = useMultiplayerStore((state) => state.opponentWantsRematch);
  const isRequestingRematch = useMultiplayerStore((state) => state.isRequestingRematch);
  const rematchOfferExpiresAt = useMultiplayerStore((state) => state.rematchOfferExpiresAt);
  const rematchNotice = useMultiplayerStore((state) => state.rematchNotice);
  const canRematch = useMultiplayerStore((state) => state.canRematch);
  const clockState = useMultiplayerStore((state) => state.clockState);
  const tickClock = useMultiplayerStore((state) => state.tickClock);
  const storeVariant = useMultiplayerStore((state) => state.variant);
  /*
   * La damiera e' la fonte autorevole: `boardState.variant` arriva da
   * `room.getState()` in OGNI payload del server, anche in quelli che si
   * dimenticano il campo di primo livello. Il campo di store e' invece «quel che
   * ha lasciato la partita precedente» — e il commento di `createQuickTable`
   * dice da quanto: «reading it here is exactly how a table opened as Italian
   * came out International». `ArenaGame` e `TournamentGame` leggono gia' cosi';
   * l'online era l'unico che si fidava dello store.
   */
  const variant = boardState?.variant ?? storeVariant;
  const timeControl = useMultiplayerStore((state) => state.timeControl);
  const storeIsRated = useMultiplayerStore((state) => state.isRated);
  const gameStartedAtMs = useMultiplayerStore((state) => state.gameStartedAtMs);
  const drawnOpening = useMultiplayerStore((state) => state.drawnOpening);
  const matchLegIndex = useMultiplayerStore((state) => state.matchLegIndex);
  const matchTotalLegs = useMultiplayerStore((state) => state.matchTotalLegs);
  const matchScore = useMultiplayerStore((state) => state.matchScore);
  const matchComplete = useMultiplayerStore((state) => state.matchComplete);
  const returnLegStartsAt = useMultiplayerStore((state) => state.returnLegStartsAt);
  const returnLegReady = useMultiplayerStore((state) => state.returnLegReady);
  const matchReadyUserIds = useMultiplayerStore((state) => state.matchReadyUserIds);
  const setReturnLegReady = useMultiplayerStore((state) => state.setReturnLegReady);

  // Disconnection state
  const isOpponentDisconnected = useMultiplayerStore((state) => state.isOpponentDisconnected);
  const primaMossaScadeAt = useMultiplayerStore((state) => state.primaMossaScadeAt);
  const primaMossaEsito = useMultiplayerStore((state) => state.primaMossaEsito);
  const openingPlyCount = useMultiplayerStore((state) => state.openingPlyCount);
  /* La posizione di partenza di una partita nata dall'editor: la manda il server,
     e serve alla revisione (vedi i due effetti qui sotto). `null` sempre, tranne
     che in una partita composta. */
  const startBoardDalServer = useMultiplayerStore((state) => state.startBoard);
  const graceTimeRemaining = useMultiplayerStore((state) => state.graceTimeRemaining);
  const opponentMaiArrivato = useMultiplayerStore((state) => state.opponentMaiArrivato);
  const opponentDecideLOrologio = useMultiplayerStore((state) => state.opponentDecideLOrologio);

  // Game-over overlay: shown when the game ends, dismissable to review the board.
  const [overlayDismissed, setOverlayDismissed] = useState(false);

  // Get current user info
  const currentUser = useAuthStore((state) => state.user);
  const isGuest = useAuthStore((state) => state.isGuest);

  // Draw offer state
  const drawOffered = useMultiplayerStore((state) => state.drawOffered);
  const opponentOfferedDraw = useMultiplayerStore((state) => state.opponentOfferedDraw);
  const drawDeclinedNotice = useMultiplayerStore((state) => state.drawDeclinedNotice);
  /* Il freno alle proposte di patta. Selettori reattivi e non `getState()`: i
     test dei componenti sostituiscono lo store con un finto che NON ha
     `.getState`. */
  const drawOffersLeft = useMultiplayerStore((state) => state.drawOffersLeft);
  const drawMustMove = useMultiplayerStore((state) => state.drawMustMove);
  const pattaBloccata = etichettePattaBloccata(drawOffersLeft, drawMustMove);

  // Takeback state (casual online only)
  const takebackOffered = useMultiplayerStore((state) => state.takebackOffered);
  const opponentRequestedTakeback = useMultiplayerStore((state) => state.opponentRequestedTakeback);
  const pendingTakebackPlies = useMultiplayerStore((state) => state.pendingTakebackPlies);
  const takebackDeclinedNotice = useMultiplayerStore((state) => state.takebackDeclinedNotice);
  const takebackAcceptedNotice = useMultiplayerStore((state) => state.takebackAcceptedNotice);
  const requestTakeback = useMultiplayerStore((state) => state.requestTakeback);
  const acceptTakeback = useMultiplayerStore((state) => state.acceptTakeback);
  const declineTakeback = useMultiplayerStore((state) => state.declineTakeback);
  // Local-only "disabled in rated games" cue, set when a takeback pickup is
  // attempted in a rated game (no socket traffic).
  const [ratedTakebackNotice, setRatedTakebackNotice] = useState(false);

  /**
   * `useCallback` e non una arrow inline, e la ragione non e' stilistica.
   *
   * Questo componente si ri-renderizza circa cinque volte al secondo, perche' sottoscrive
   * `clockState` e l'orologio ne produce uno nuovo a ogni tick. Una arrow inline sarebbe
   * una prop NUOVA a ogni giro, e `memo(GameBoard)` — che esiste apposta contro questo,
   * vedi la nota in fondo a GameBoard.tsx — fallirebbe cinque volte al secondo,
   * ricostruendo caselle, pezzi, defs e insiemi delle destinazioni.
   *
   * `ArenaGame` e `TournamentGame` non hanno mai avuto il difetto: la forma giusta era
   * gia' in casa, e un test la blocca.
   */
  const segnalaTakebackBloccato = useCallback(() => setRatedTakebackNotice(true), []);

  // Chat unread badge + active flag for the mobile full-screen chat overlay.
  const unreadCount = useChatStore((state) => state.unreadCount);
  const { count: spectatorCount } = useGameSpectators();
  const setChatActive = useChatStore((state) => state.setActive);

  const makeMove = useMultiplayerStore((state) => state.makeMove);
  const resign = useMultiplayerStore((state) => state.resign);
  const resyncGame = useMultiplayerStore((state) => state.resyncGame);
  const errore = useMultiplayerStore((state) => state.error);
  const clearError = useMultiplayerStore((state) => state.clearError);
  const reset = useMultiplayerStore((state) => state.reset);
  const requestRematch = useMultiplayerStore((state) => state.requestRematch);
  const declineRematch = useMultiplayerStore((state) => state.declineRematch);
  const offerDraw = useMultiplayerStore((state) => state.offerDraw);
  const acceptDraw = useMultiplayerStore((state) => state.acceptDraw);
  const declineDrawAction = useMultiplayerStore((state) => state.declineDraw);

  // Sync multiplayer board state to local game store for rendering
  const syncToLocalStore = useGameStore((state) => state.syncFromExternal);

  const currentTurn = boardState?.currentTurn ?? Color.WHITE;
  const gameStatus = boardState?.status ?? GameStatus.PLAYING;

  // Log mount
  useEffect(() => {
    log.info('OnlineGame', 'Mounted', { roomId, playerColor });
  }, []);

  /*
   * L'azione si legge con un SELETTORE, non con `useGameStore.getState()`.
   *
   * I test di questi tre wrapper montano un finto store che è solo un selettore
   * (`useGameStore(selector)`), senza `.getState`: chiamarlo qui fa cadere il
   * componente in prova — cioè un difetto che si scopre solo dopo. È una lezione
   * già pagata in questo repo.
   */
  const enterOnlineGame = useGameStore((s) => s.enterOnlineGame);
  const setOnlineStartBoard = useGameStore((s) => s.setOnlineStartBoard);

  // Online play is player-vs-player: reset the shared game store to 'pvp' so a
  // leftover AI mode from an earlier local game (gameMode is never reset when
  // switching AI → online) can't block review navigation or suppress the go-live
  // blink. Board state itself comes from the server via syncFromExternal.
  //
  // La posizione di partenza si passa GIA' QUI, e non solo nell'effetto qui
  // sotto: al montaggio il `game:start` e' gia' arrivato (e' lui a portare su
  // questa schermata), quindi il valore c'e'; passarlo rende la ricostruzione
  // della revisione indipendente dall'ordine in cui girano i due effetti.
  useEffect(() => {
    enterOnlineGame(startBoardDalServer);
  }, []);

  /*
   * E la si riapplica quando CAMBIA, perche' l'effetto qui sopra gira una volta
   * sola: `OnlineGame` si monta una volta (App non gli da' una `key`) e le
   * partite successive arrivano a schermata montata — rivincita, ritorno di
   * manche, rientro dopo un buco di rete. Senza questo, la revisione della
   * rivincita di una partita composta ricostruirebbe dall'apertura standard.
   *
   * Vale anche al contrario: una partita NORMALE dopo una composta porta `null`
   * e spegne la posizione di prima.
   */
  useEffect(() => {
    setOnlineStartBoard(startBoardDalServer);
  }, [startBoardDalServer, setOnlineStartBoard]);

  // Re-arm the game-over overlay for every new game in this room: gameStartedAtMs
  // changes on game:start, rematch:start and match:leg-start. Without this, a
  // player who dismissed the previous overlay (e.g. during the return-leg
  // countdown) would never see the next game's result.
  useEffect(() => {
    setOverlayDismissed(false);
    // Stesso momento, stessa ragione: `gameStartedAtMs` cambia su game:start,
    // rematch:start e match:leg-start, cioè ogni volta che comincia una partita nuova,
    // ed è lì che il conto delle sbirciate deve ripartire da zero.
    useBlindStore.getState().resetBlindSession();
  }, [gameStartedAtMs]);

  // Auto-dismiss del rifiuto della patta. Piu' lungo dei 2,5s del rifai-mossa:
  // arriva mentre si sta pensando alla mossa, non subito dopo un proprio gesto.
  useEffect(() => {
    if (!drawDeclinedNotice) return;
    const id = window.setTimeout(
      () => useMultiplayerStore.setState({ drawDeclinedNotice: false }),
      DRAW_DECLINE_NOTICE_MS,
    );
    return () => window.clearTimeout(id);
  }, [drawDeclinedNotice]);

  // Auto-dismiss the transient takeback notices.
  useEffect(() => {
    if (!takebackDeclinedNotice) return;
    const id = window.setTimeout(() => useMultiplayerStore.setState({ takebackDeclinedNotice: false }), 2500);
    return () => window.clearTimeout(id);
  }, [takebackDeclinedNotice]);

  useEffect(() => {
    if (!takebackAcceptedNotice) return;
    const id = window.setTimeout(() => useMultiplayerStore.setState({ takebackAcceptedNotice: false }), 2500);
    return () => window.clearTimeout(id);
  }, [takebackAcceptedNotice]);

  useEffect(() => {
    if (!ratedTakebackNotice) return;
    const id = window.setTimeout(() => setRatedTakebackNotice(false), 3000);
    return () => window.clearTimeout(id);
  }, [ratedTakebackNotice]);

  // Sync board state when it changes
  useEffect(() => {
    if (boardState && syncToLocalStore) {
      log.info('OnlineGame', 'Sync board to local store', {
        turn: boardState.currentTurn,
        moves: boardState.moveHistory?.length ?? 0,
      });
      syncToLocalStore(boardState);
    }
  }, [boardState, syncToLocalStore]);

  /*
   * Si ha una partita in mano, ma lo stato non e' ne' giocabile ne' concluso.
   *
   * E' il buco in cui si cadeva rientrando dal secondo piano: 'connected' o
   * 'disconnected' con la damiera ancora a schermo. Da qui la damiera diventa
   * inerte (vedi `boardInteractive`), compare l'avviso e parte il resync.
   */
  const statoNonGiocabile = Boolean(roomId) && Boolean(boardState) && status !== 'playing' && status !== 'ended';
  const sessioneAltrove = useSessioneAltrove();

  /*
   * Cane da guardia del rientro.
   *
   * Se lo stato resta incoerente, si chiede al server come stanno le cose
   * invece di aspettare un evento che potrebbe non arrivare mai — `game:end` va
   * alla room, e chi si e' disconnesso l'ha gia' lasciata. `resyncGame` a
   * socket giu' non interroga: sveglia la connessione.
   *
   * ⚠️ RIPETUTO, non un colpo solo. Il tentativo unico l'ho visto fallire in un
   * browser vero: staccata la rete per due minuti, socket.io esaurisce i suoi
   * quindici tentativi e si ferma per sempre; l'unico risveglio previsto sta
   * sul cambio di visibilita', che una rete che torna non produce. Il primo
   * colpo cadeva mentre la rete era ancora giu' e nessuno riprovava piu': la
   * damiera restava li', col suo avviso, all'infinito.
   */
  useEffect(() => {
    if (!statoNonGiocabile) return;
    const id = window.setInterval(() => {
      log.warn('OnlineGame', 'stato non giocabile: chiedo il resync');
      void resyncGame();
    }, ATTESA_PRIMA_DEL_RESYNC_MS);
    return () => window.clearInterval(id);
  }, [statoNonGiocabile, resyncGame]);

  const isOurTurn =
    (playerColor === 'white' && currentTurn === Color.WHITE) ||
    (playerColor === 'black' && currentTurn === Color.BLACK);

  const isGameOver = gameStatus !== GameStatus.PLAYING || status === 'ended';

  /*
   * La sentinella della damiera ferma.
   *
   * Il watchdog qui sopra copre lo stato NON giocabile; questo copre l'opposto,
   * che e' il caso peggiore perche' non sembra un guasto: `'playing'`, damiera a
   * schermo, e una mossa dell'avversario che non e' mai arrivata. Legge dallo
   * store gia' letto qui — niente `getState()`, che i finti store dei test di
   * questo wrapper non hanno.
   */
  const preMoveBoardState = useMultiplayerStore((state) => state.preMoveBoardState);
  useGuardiaSincronia({
    attiva: status === 'playing' && !isGameOver,
    turnoDamiera: boardState ? (boardState.currentTurn === Color.WHITE ? 'white' : 'black') : null,
    turnoOrologio: clockState?.activePlayer ?? null,
    mossaInVolo: preMoveBoardState != null,
    resync: resyncGame,
    contesto: 'online',
  });

  // Re-arm the overlay for each new game so it re-appears when the next ends.
  useEffect(() => {
    if (!isGameOver) setOverlayDismissed(false);
  }, [isGameOver]);

  const handleMove = useCallback((move: Move) => {
    if (isOurTurn && !isGameOver) {
      log.info('OnlineGame', 'Move sent', { from: move.from, to: move.to });
      makeMove(move);
    }
  }, [isOurTurn, isGameOver, makeMove]);

  /* `useCallback` perche' finisce dentro gli slot memoizzati qui sotto: senza,
     la sua identita' nuova a ogni render li farebbe ricostruire, e con loro
     cadrebbe il `memo` su `GameScreen`. */
  const handleLeave = useCallback(() => {
    reset();
    onLeave();
  }, [reset, onLeave]);

  // Track game completed when game ends
  useEffect(() => {
    if (status === 'ended' && gameResult && boardState) {
      log.info('OnlineGame', 'Game sync - ended', { gameResult, reason: gameEndReason });
      const myResult = gameResult === 'draw' ? 'draw' : gameResult === playerColor ? 'win' : 'loss';
      trackGameCompleted({
        variant,
        timeControl: timeControl ?? 'none',
        result: myResult,
        mode: 'multiplayer',
        moves: boardState.moveHistory?.length ?? 0,
        durationSeconds: gameStartedAtMs ? Math.round((Date.now() - gameStartedAtMs) / 1000) : 0,
        ruleMode: boardState.ruleMode,
      });
    }
  }, [status]);

  const handleRequestRematch = useCallback(() => {
    if (isRequestingRematch) return;
    log.info('OnlineGame', 'Rematch requested');
    requestRematch();
    trackRematchRequested();
  }, [isRequestingRematch, requestRematch]);

  const getTurnText = useCallback(() => {
    if (isGameOver) {
      /*
       * `gameResult` PRIMA del verdetto, non dopo.
       *
       * `isGameOver` diventa vero già con `boardState.status`, che arriva col
       * `game:update` che il server emette PRIMA di chiudere la partita; il
       * verdetto (`gameResult`) arriva solo con `game:end`, e fra i due c'è una
       * transazione con lock di riga, Glicko-2, le scritture di arena/torneo e
       * l'invalidazione della cache, su un droplet a 1 vCPU. Senza questa
       * guardia il ramo finale vinceva per esclusione e IL VINCITORE leggeva
       * «Hai Perso!» — nel titolo del riquadro di fine partita, in rosso — per
       * poi vederlo cambiare.
       */
      if (!gameResult) return t('game.result.gameOver');
      if (gameResult === 'draw') return t('game.result.draw');
      if (gameResult === playerColor) return t('game.result.youWin');
      return t('game.result.youLose');
    }
    if (isOurTurn) return t('game.turn.yourTurn');
    return t('game.turn.opponentTurn');
  }, [gameResult, isGameOver, isOurTurn, playerColor, t]);

  const getResultClass = useCallback((): string => {
    if (!isGameOver) return '';
    // Stessa guardia di `getTurnText`: fra `game:update` e `game:end` la partita e'
    // gia' finita ma il verdetto non c'e', e il ramo finale dava «sconfitta» al vincitore.
    if (!gameResult) return '';
    if (gameResult === 'draw') return 'draw';
    if (gameResult === playerColor) return 'win';
    return 'loss';
  }, [gameResult, isGameOver, playerColor]);

  // Win-condition modifier, carried on the board state from game:start.
  const ruleMode = boardState?.ruleMode ?? 'normal';

  // Chi ha perso: serve al motivo, che il server manda senza lato ("resignation",
  // non "il Bianco ha abbandonato"). Assente sulle patte, dove nessuno perde.
  const loserColor = gameResult === 'white' ? Color.BLACK : gameResult === 'black' ? Color.WHITE : undefined;
  const getResultMessage = useCallback(() => endReasonText(t, gameEndReason, { loser: loserColor, ruleMode }), [t, gameEndReason, loserColor, ruleMode]);

  const color = playerColor ?? 'white';

  /*
   * Player info, MEMOIZZATE. Questo componente si ri-renderizza circa cinque
   * volte al secondo (vedi la nota su `segnalaTakebackBloccato`), e queste due
   * scendono a `GameScreen`, che e' memoizzato: un oggetto nuovo a ogni giro lo
   * farebbe ri-renderizzare insieme all'orologio, cioe' renderebbe il `memo`
   * peso morto. Stessa ragione, e stessa cura, di `LocalGame`.
   */
  const topPlayer = useMemo(() => ({
    username: opponent?.username || t('common.anonymous'),
    rating: opponent?.rating,
    avatar: opponent?.avatar ?? null,
    country: opponent?.country ?? null,
    // The real account name, so the card can link to the profile. Null when
    // unknown — `username` above may be the localized "Anonymous" placeholder.
    profileUsername: opponent?.username ?? null,
  }), [opponent?.username, opponent?.rating, opponent?.avatar, opponent?.country, t]);
  const myRating = getRatingForGame(currentUser, variant, timeControl ?? '');
  const bottomPlayer = useMemo(() => ({
    username: currentUser?.username || t('common.you'),
    rating: myRating,
    avatar: currentUser?.avatar ?? null,
    country: currentUser?.country ?? null,
    profileUsername: currentUser?.username ?? null,
  }), [currentUser?.username, myRating, currentUser?.avatar, currentUser?.country, t]);

  // Log player info for debugging
  useEffect(() => {
    log.info('OnlineGame', 'Player info resolved', {
      topPlayer: { username: topPlayer.username, rating: topPlayer.rating },
      bottomPlayer: { username: bottomPlayer.username, rating: myRating },
      variant,
      timeControl,
      storeOpponent: opponent ? { username: opponent.username, rating: opponent.rating } : null,
    });
  }, [opponent?.username, opponent?.rating, myRating, variant, timeControl]);

  // Metadata for GameScreen header (variant / time control / rated)
  const variantProp = variant.toLowerCase() as 'international' | 'italian' | 'english';
  // Resolve rather than index the preset table: a direct challenge can be played
  // at any cadence, and TIME_CONTROL_PRESETS only knows the 19 canonical ones
  // (indexing it threw on anything else). Untimed games name themselves instead
  // of falling through to the header's em-dash placeholder.
  const timeControlLabel = !timeControl
    ? undefined
    : timeControl === 'none'
      ? t('game.timeControls.noTimeLimit')
      : resolveTimeControl(timeControl)?.name;
  // A game is rated only when the server marked it rated (an "amichevole"/casual
  // challenge sends rated=false) AND both players are registered humans. The
  // opponent is unrated when they are a guest or a bot — both use the reserved
  // `Guest_` prefix — mirroring the server-side rating policy (shouldRateGame).
  // `storeIsRated !== false` treats a missing flag (older server) as rated so we
  // fall back to the guest/bot heuristic; only an explicit false forces casual.
  const isRatedGame = storeIsRated !== false && !isGuest && !isGuestUsername(opponent?.username);

  // Takeback is permitted only in CASUAL games (server marked rated=false). This is
  // the RAW casual flag — unlike `isRatedGame` above it does NOT fold in guest/bot,
  // because a casual game vs a guest/bot is still eligible (the bot just declines).
  const takebackAllowed = storeIsRated === false;
  const takebackMessage = ratedTakebackNotice
    ? t('game.actions.takebackDisabledRated', 'Takeback is disabled in rated games')
    : takebackAcceptedNotice
      ? t('game.actions.takebackAccepted', 'Your takeback was accepted')
      : takebackDeclinedNotice
        ? t('game.actions.takebackDeclined', 'Your takeback was declined')
        : takebackOffered
          ? t('game.actions.takebackRequested', 'Takeback requested…')
          : null;

  // Build turn text with end reason
  const turnTextFull = isGameOver && gameEndReason
    ? `${getTurnText()} (${getResultMessage()})`
    : getTurnText();

  // The return leg of a two-leg match starts on its own — while it's pending the
  // player gets a countdown instead of the (inapplicable) rematch panel.
  const isReturnLegPending = returnLegStartsAt !== null;
  const opponentReadyForReturnLeg =
    matchReadyUserIds.length > 0 && matchReadyUserIds.some((id) => id !== currentUser?.id);

  // Actions slot: rematch panel (when game over), or the return-leg countdown.
  const chiudiRiquadroFine = useCallback(() => setOverlayDismissed(true), []);

  const actionsContent = useMemo(() => (isGameOver && playerColor ? (
    isReturnLegPending ? (
      <ReturnLegCountdown
        startsAt={returnLegStartsAt}
        score={matchScore}
        ready={returnLegReady}
        opponentReady={opponentReadyForReturnLeg}
        onReady={setReturnLegReady}
        onLeave={handleLeave}
        rated={isRatedGame && !!drawnOpening}
      />
    ) : (
      <RematchPanel
        sessionScore={sessionScore}
        playerColor={playerColor}
        opponentWantsRematch={opponentWantsRematch}
        isRequestingRematch={isRequestingRematch}
        canRematch={canRematch}
        offerExpiresAt={rematchOfferExpiresAt}
        notice={rematchNotice}
        matchScore={matchScore}
        matchComplete={matchComplete}
        onRequestRematch={handleRequestRematch}
        onDeclineRematch={declineRematch}
        onNewGame={handleLeave}
      />
    )
  ) : undefined), [isGameOver, playerColor, isReturnLegPending, returnLegStartsAt, matchScore,
    returnLegReady, opponentReadyForReturnLeg, setReturnLegReady, handleLeave,
    isRatedGame, drawnOpening, sessionScore, opponentWantsRematch, isRequestingRematch,
    canRematch, rematchOfferExpiresAt, rematchNotice, matchComplete, handleRequestRematch,
    declineRematch]);

  // Game-over overlay, sized like the resign dialog, reusing the rematch actions.
  const gameOverOverlay = useMemo(() => (
    <GameOverOverlay
      open={isGameOver && !overlayDismissed}
      result={getResultClass() as 'win' | 'loss' | 'draw' | ''}
      title={getTurnText()}
      // Il riquadro diceva solo chi aveva vinto: il perché finiva nella riga del turno,
      // che a fine partita è coperta proprio da questo riquadro.
      reason={getResultMessage()}
      onClose={chiudiRiquadroFine}
    >
      <BlindPeekSummary blind={ruleMode === 'blind'} />
      {/* Il riquadro copre il pannello rivincita, e chi RICEVE la proposta e'
          quasi sempre ancora qui sopra (chi la manda ha dovuto chiuderlo per
          premere il bottone). Senza questa copia la proposta non si vedeva
          affatto. Stesso motivo per cui ReturnLegCountdown sta in due posti. */}
      {opponentWantsRematch && canRematch && !isReturnLegPending && (
        <IncomingRematchPrompt onAccept={handleRequestRematch} onDecline={declineRematch} />
      )}
      {isReturnLegPending && (
        <ReturnLegCountdown
          startsAt={returnLegStartsAt}
          score={matchScore}
          ready={returnLegReady}
          opponentReady={opponentReadyForReturnLeg}
          onReady={setReturnLegReady}
          onLeave={handleLeave}
          rated={isRatedGame && !!drawnOpening}
        />
      )}
    </GameOverOverlay>
  ), [isGameOver, overlayDismissed, getResultClass, getTurnText, getResultMessage,
    chiudiRiquadroFine, isReturnLegPending, returnLegStartsAt, matchScore, returnLegReady,
    opponentReadyForReturnLeg, setReturnLegReady, handleLeave, isRatedGame, drawnOpening,
    canRematch, declineRematch, handleRequestRematch, opponentWantsRematch, ruleMode]);

  /* Memoizzato: scende a `GameScreen`, che e' memoizzato, e questo componente
     si ri-renderizza cinque volte al secondo per via dell'orologio. */
  const boardOverlaySlot = useMemo(() => (
        <>
          <DrawnOpeningBanner
            opening={drawnOpening}
            triggerKey={gameStartedAtMs}
            /* Accanto all'«apertura sorteggiata» basta «1/2»; da solo — match a
               mossa libera, dove il banner non ha altro da dire — quel numero non
               si capirebbe, e serve la frase intera. */
            legLabel={
              matchTotalLegs && matchTotalLegs > 1
                ? t(drawnOpening ? 'online.match.legShort' : 'online.match.legOf',
                    { leg: matchLegIndex ?? 1, total: matchTotalLegs })
                : undefined
            }
          />
          {isReturnLegPending && (
            <ReturnLegCountdown
              compact
              startsAt={returnLegStartsAt}
              score={matchScore}
              ready={returnLegReady}
              opponentReady={opponentReadyForReturnLeg}
              onReady={setReturnLegReady}
            />
          )}
          <TakebackNotice message={takebackMessage} />
          <AvvisoErrore errore={errore} onChiudi={clearError} />
          {/* Con la sessione su un altro dispositivo la connessione non e' persa:
              e' stata ceduta, e «Riprova» non potrebbe riuscire. Lo dice
              `AvvisoAltroDispositivo`, globale, con l'unico comando che serve. */}
          <ConnessionePersaBanner
            attiva={statoNonGiocabile && !sessioneAltrove}
            onRiprova={() => { void resyncGame(); }}
            onEsci={handleLeave}
          />
        </>
  ), [drawnOpening, gameStartedAtMs, matchTotalLegs, matchLegIndex, t, isReturnLegPending,
      returnLegStartsAt, matchScore, returnLegReady, opponentReadyForReturnLeg, setReturnLegReady,
      takebackMessage, errore, clearError, statoNonGiocabile, sessioneAltrove, resyncGame, handleLeave]);

  const sidebarSlot = useMemo(
    () => (
      <GameSidebarTabs
        opening={drawnOpening}
        openingPlyCount={openingPlyCount}
      />
    ),
    /* `openingPlyCount` NELLE DIPENDENZE, e non e' una formalita': il conto arriva
       dopo il mount (con `game:start`), e un memo che non lo guarda tiene la colonna
       al valore di partenza. `drawnOpening` lo salverebbe quasi sempre, ma non nel
       rientro in cui il server manda il conto con l'apertura rimasta `null`. */
    [drawnOpening, openingPlyCount],
  );

  // Mobile-only full-screen panels (desktop keeps the GameSidebarTabs sidebar).
  // Opening the chat marks it active so its unread badge clears; closing it
  // re-enables the badge for incoming opponent messages.
  const mobilePanels: MobilePanel[] = useMemo(() => [
    {
      id: 'moves',
      label: t('game.sidebar.moves', 'Moves'),
      icon: <List size={20} aria-hidden="true" />,
      render: () => <MoveList opening={drawnOpening} />,
    },
    {
      id: 'chat',
      label: t('game.sidebar.chat', 'Chat'),
      icon: <MessageSquare size={20} aria-hidden="true" />,
      badge: unreadCount,
      onOpen: () => setChatActive(true),
      onClose: () => setChatActive(false),
      render: () => <ChatPanel />,
    },
    /* L'occhio sta a destra della Chat, e c'è anche quando non guarda nessuno:
       sapere di NON essere guardati è un'informazione quanto il contrario. Di
       sola icona perché a 360px la barra non regge una terza parola. */
    {
      id: 'spectators',
      label: t('game.sidebar.spectators', 'Spectators'),
      icon: <Eye size={20} aria-hidden="true" />,
      badge: spectatorCount,
      compact: true,
      render: () => <SpectatorsPanel />,
    },
  ], [t, drawnOpening, unreadCount, spectatorCount, setChatActive]);

  return (
    <GameScreen
      boardState={boardState}
      playerColor={color}
      isMyTurn={isOurTurn}
      isGameOver={isGameOver}
      flipped={color === 'black'}
      onMove={handleMove}
      allowPremove
      clockState={clockState}
      isPlaying={status === 'playing'}
      boardInteractive={status === 'playing'}
      tickClock={tickClock}
      topPlayer={topPlayer}
      bottomPlayer={bottomPlayer}
      turnText={turnTextFull}
      resultClass={getResultClass()}
      isOpponentDisconnected={isOpponentDisconnected}
      graceTimeRemaining={graceTimeRemaining}
      opponentMaiArrivato={opponentMaiArrivato}
      opponentDecideLOrologio={opponentDecideLOrologio}
      primaMossaScadeAt={primaMossaScadeAt}
      primaMossaEsito={primaMossaEsito}
      /* Le semi-mosse dell'apertura non sono mosse di nessuno: senza questo, una
         partita con apertura sembrerebbe già cominciata e il conto sparirebbe.
         Dallo store e non da `drawnOpening`, che esiste solo per il sorteggio:
         vedi la nota su `openingPlyCount` in multiplayer-store. */
      openingPlyCount={openingPlyCount}
      actionsSlot={actionsContent}
      overlaySlot={gameOverOverlay}
      boardOverlaySlot={boardOverlaySlot}
      sidebarSlot={sidebarSlot}
      mobilePanels={mobilePanels}
      closePanelsKey={gameStartedAtMs}
      onResign={resign}
      onOfferDraw={offerDraw}
      onAcceptDraw={acceptDraw}
      onDeclineDraw={declineDrawAction}
      drawOffered={drawOffered}
      opponentOfferedDraw={opponentOfferedDraw}
      /*
       * La pastiglia dice solo il RIFIUTO, come prima: cosa fare lo dice il
       * bottone, che e' due centimetri sotto e nello stesso sguardo («Muovi
       * prima»). Concatenare le due frasi dava una pastiglia di cinque righe a
       * 360px che ripeteva «patta» tre volte — e un trattino che incolla due
       * frasi e' una traduzione che non regge in otto lingue.
       */
      offerNotice={drawDeclinedNotice ? t('game.actions.drawDeclined') : null}
      drawBlockedLabel={pattaBloccata ? t(pattaBloccata.label) : null}
      drawBlockedReason={pattaBloccata ? t(pattaBloccata.reason) : null}
      takebackAllowed={takebackAllowed}
      onTakebackRequest={requestTakeback}
      onRatedTakebackBlocked={segnalaTakebackBloccato}
      opponentRequestedTakeback={opponentRequestedTakeback}
      pendingTakebackPlies={pendingTakebackPlies}
      onAcceptTakeback={acceptTakeback}
      onDeclineTakeback={declineTakeback}
      variant={variantProp}
      timeControlLabel={timeControlLabel}
      timeControlPreset={timeControl}
      isRated={isRatedGame}
      ruleMode={ruleMode}
    />
  );
}

export default OnlineGame;
